<script lang="ts">
    import EntitySelector from '$lib/components/common/selectors/EntitySelector.svelte';
    import Button from '$lib/components/common/button/Button.svelte';
    import {showToast} from '$lib/helpers/toastrHelper';
    import {fetchWithAuth} from '$lib/utils/http/httpUtil';

    export let clientId: string = '';
  export let entityId: string = '';
  export let clientName: string = '';
  export let organisationId: string | undefined;

  let selectedEntityId = entityId;
  let generatedUrl = '';
  let isGenerating = false;
  let checkedEntityId = '';
  let hasExistingConfig = false;
  let isForcingSync = false;
  let checkedClientIdForAnyConfig = '';
  let hasAnyExistingConfig = false;

  $: canGenerate = !!(clientId && selectedEntityId);

  $: if (selectedEntityId !== checkedEntityId) {
    generatedUrl = '';
    if (selectedEntityId) {
      checkExistingConfig(selectedEntityId);
    } else {
      checkedEntityId = '';
      hasExistingConfig = false;
    }
  }

  // Force sync is a client-wide action (queues every entity belonging to the client, not just one), so
  // its availability shouldn't depend on which entity happens to be selected in the dropdown above - only
  // on whether this client has any Blackbaud entity configured at all.
  $: if (clientId && clientId !== checkedClientIdForAnyConfig) {
    checkAnyExistingConfig(clientId);
  }

  async function checkAnyExistingConfig(targetClientId: string) {
    try {
      const response = await fetchWithAuth(`/api/blackbaud/config/${targetClientId}`);
      const result = await response.json();

      if (clientId === targetClientId) {
        hasAnyExistingConfig = !!result.exists;
        checkedClientIdForAnyConfig = targetClientId;
      }
    } catch {
      if (clientId === targetClientId) {
        hasAnyExistingConfig = false;
        checkedClientIdForAnyConfig = targetClientId;
      }
    }
  }

  async function checkExistingConfig(targetEntityId: string) {
    if (!clientId) return;

    try {
      const response = await fetchWithAuth(
        `/api/blackbaud/config/${clientId}/${targetEntityId}`,
      );
      const result = await response.json();

      // Ignore stale responses if the selection has moved on to a different entity in the meantime.
      if (selectedEntityId === targetEntityId) {
        hasExistingConfig = !!result.exists;
        checkedEntityId = targetEntityId;
      }
    } catch {
      if (selectedEntityId === targetEntityId) {
        hasExistingConfig = false;
        checkedEntityId = targetEntityId;
      }
    }
  }

  async function generateUrl() {
    if (!canGenerate) return;

    isGenerating = true;
    generatedUrl = '';

    try {
      const response = await fetchWithAuth('/api/blackbaud/generate-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: parseInt(clientId),
          entityId: parseInt(selectedEntityId),
        }),
      });

      const result = await response.json();

      if (result.success) {
        generatedUrl = result.url;
        showToast({ title: 'Success', message: 'Authentication link generated successfully', type: 'success' });
      } else {
        showToast({ title: 'Error', message: result.error || 'Failed to generate authentication link', type: 'error' });
      }
    } catch (error) {
      showToast({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to generate authentication link',
        type: 'error',
      });
    } finally {
      isGenerating = false;
    }
  }

  async function forceSync() {
    if (!clientId) return;

    isForcingSync = true;

    try {
      const response = await fetchWithAuth(`/api/blackbaud/clients/${clientId}/force-sync`, {
        method: 'POST',
      });

      if (response.ok) {
        showToast({ title: 'Success', message: 'Force sync queued for all entities on this client', type: 'success' });
      } else {
        showToast({ title: 'Error', message: 'Failed to queue force sync', type: 'error' });
      }
    } catch (error) {
      showToast({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to queue force sync',
        type: 'error',
      });
    } finally {
      isForcingSync = false;
    }
  }

  function copyToClipboard() {
    if (!generatedUrl) return;
    navigator.clipboard.writeText(generatedUrl).then(() => {
      showToast({ title: 'Copied', message: 'Link copied to clipboard', type: 'success' });
    });
  }
</script>

<div class="p-5">
  <h3 class="mb-4 text-xl font-semibold">Blackbaud Integration Setup</h3>
  <p class="mb-4 text-sm text-surface-700">
    Generate an authentication link to connect <strong>{clientName}</strong>'s Blackbaud account to Alii.
  </p>

  <div class="mb-4 flex justify-end gap-2">
    <Button
      label={isForcingSync ? 'Queueing...' : 'Force sync'}
      onClick={forceSync}
      disabled={!clientId || isForcingSync || !hasAnyExistingConfig}
      size="small"
    />
  </div>

  <div class="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
    <h4 class="mb-4 font-semibold">Select Entity</h4>
    <p class="mb-4 text-sm text-gray-600">Choose the entity to connect to Blackbaud.</p>

    <EntitySelector bind:selectedEntityId {clientId} isRequired={true} />
  </div>

  {#if hasExistingConfig}
    <div class="mb-6 flex items-start gap-2 rounded-lg border border-yellow-300 bg-yellow-50 p-4 text-sm text-yellow-800">
      <svg class="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4m0 4h.01" />
      </svg>
      <span>This entity is already connected to Blackbaud. Generating a new link will re-authenticate and replace the existing connection.</span>
    </div>
  {/if}

  <div class="mb-6 flex gap-2">
    <Button
      label={isGenerating ? 'Generating...' : 'Generate Authentication Link'}
      onClick={generateUrl}
      colour="primary"
      disabled={!canGenerate || isGenerating}
    />
  </div>

  {#if generatedUrl}
    <div class="space-y-3 rounded-lg border border-primary-200 bg-primary-50 p-4">
      <h4 class="font-semibold text-primary-800">Authentication Link</h4>
      <p class="text-sm text-gray-700">
        Copy this link and send it to the client so they can connect their Blackbaud account to Alii. They'll
        need to log into Blackbaud and approve the requested access.
      </p>
      <div class="break-all rounded border border-gray-300 bg-white p-3 font-mono text-sm">
        {generatedUrl}
      </div>
      <div class="flex gap-2">
        <Button label="Copy to Clipboard" onClick={copyToClipboard} colour="secondary" size="small" />
        <a href={generatedUrl} target="_blank" rel="noopener noreferrer" class="btn variant-ghost-primary btn-sm">
          Open in New Tab
        </a>
      </div>
    </div>
  {:else if !canGenerate}
    <div class="rounded-lg bg-gray-100 p-4">
      <p class="text-sm text-gray-700">Please select an entity to generate an authentication link.</p>
    </div>
  {/if}
</div>
